import { type LineItem } from "~/features/cart/cart/models/CartData";
import { type ProductData } from "~/domains/product/types/ProductData";
import { B2CProductListItem } from "~/features/cart/B2CCart/components/B2CProductList/compoments/B2CProductListItem";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { IconButton, styled } from "~/common/mui";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";

const PRODUCT_SIZE_S = "196px";
const PRODUCT_SIZE_M = "228px";
const PRODUCT_SIZE_L = "272px";
const QTY_SIZE_S = "180px";
const QTY_SIZE_M = "212px";
const QTY_SIZE_L = "256px";
const TABLE_HEADER_BORDER = 3;

const Root = styled("div")({
  display: "flex",
  flexFlow: "column",
});

const ItemList = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  "& > *": {
    flex: 1,
  },
  [theme.breakpoints.up("desktop-s")]: {
    gap: 0,
    "& > *:first-child": {
      marginTop: theme.spacing(-2),
    },
  },
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  fontWeight: "bold",
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  borderBottom: `${TABLE_HEADER_BORDER}px solid ${theme.palette.divider}`,
  "--product-size": PRODUCT_SIZE_S,
  "--qty-size": QTY_SIZE_S,
  "& > *:nth-child(1)": {
    flex: "none",
    width: "calc(var(--product-size) + var(--gutter))",
  },
  "& > *:nth-child(2)": {
    flex: "none",
    paddingLeft: theme.spacing(2),
    width: `calc(${theme.spacing(2)} + var(--qty-size))`,
  },
  "& > *:nth-child(3)": {
    paddingLeft: "var(--gutter)",
    flex: 1,
  },
  "& > *:nth-child(4)": {
    flex: "none",
  },
  [theme.breakpoints.up("desktop-m")]: {
    "--product-size": PRODUCT_SIZE_M,
    "--qty-size": QTY_SIZE_M,
  },
  [theme.breakpoints.up("desktop-l")]: {
    "--product-size": PRODUCT_SIZE_L,
    "--qty-size": QTY_SIZE_L,
  },
}));

const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  margin: theme.spacing(-1.5),
  marginRight: theme.spacing(0.5),
}));

interface Props {
  className?: string;
  items: {
    data: LineItem;
    product: ProductData;
  }[];
  onDelete?: (item: LineItem) => void;
  onChange?: (item: LineItem, changes: Partial<LineItem>) => void;
  onDeleteAll?: () => void;
}

export const B2CProductList = ({ items, className, onDeleteAll, onDelete, onChange }: Props) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  return (
    <Root className={className}>
      {isDesktop && (
        <Header>
          <div>{t("cartPage.table.header.product.title")}</div>
          <div>{t("cartPage.table.header.quantity.title")}</div>
          <div>{t("cartPage.report.comment")}</div>
          <div>
            <IconButtonStyled color={"primary"} onClick={onDeleteAll}>
              <DeleteIcon  />
            </IconButtonStyled>
          </div>
        </Header>
      )}
      <ItemList>
        {items.map((item) => (
          <B2CProductListItem
            key={item.data.id}
            {...item}
            onDelete={() => onDelete?.(item.data)}
            onChange={(updates) => onChange?.(item.data, updates)}
          />
        ))}
      </ItemList>
    </Root>
  );
};
